import GroupPresentationForm from "./components/GroupPresentationForm";
import Loading from "../core/Loading";
import SoloPresentationForm from "./components/SoloPresentationForm";
import { CategoryTypes } from "../../interfaces/enums";
import { PresentationFormProps } from "./types/PresentationFormTypes";
import { useApplicationContext } from "../../contexts/ApplicationContext";
import { useCategory } from "../../hooks/useCategory";
import { useParams } from "react-router-dom";
/**
 * ============================================================================
 * PRESENTATION FORM CONTROLLER - GŁÓWNY KONTROLER FORMULARZA PREZENTACJI
 * ============================================================================
 *
 * CEL KOMPONENTU:
 * - Główny punkt wejścia dla formularzy dodawania/edycji prezentacji
 * - Implementuje Strategy Pattern - wybiera odpowiedni formularz na podstawie typu kategorii
 * - Zarządza stanem ładowania danych i routingiem
 * - Obsługuje zarówno tryb dodawania nowych prezentacji jak i edycji istniejących
 *
 * ARCHITEKTURA:
 * - Strategy Pattern: SoloPresentationForm vs GroupPresentationForm
 * - Composition Pattern: Łączy różne komponenty w spójny interfejs
 * - Loading State Management: Wyświetla loading podczas pobierania danych
 *
 * PRZEPŁYW DANYCH:
 * 1. Pobiera categoryId z URL (useParams)
 * 2. Pobiera application z kontekstu (useApplicationContext)
 * 3. Pobiera category na podstawie contestId i categoryId (useCategory)
 * 4. Sprawdza czy wszystkie dane są załadowane
 * 5. Wybiera odpowiedni formularz na podstawie category.type
 *
 * TYPY KATEGORII:
 * - SOLO: Każdy zawodnik = osobna prezentacja (SoloPresentationForm)
 * - DUO/TRIO/OTHER: Grupa zawodników = jedna prezentacja (GroupPresentationForm)
 */


export default function PresentationFormControllerNew({
  initialData,
  onSubmit,
  onCancel,
  editableMode = true,
  registrationBlocked = false,
}: PresentationFormProps) {
  // ============================================================================
  // POBRANIE DANYCH Z KONTEKSTÓW I ROUTINGU
  // ============================================================================

  // Pobierz ID kategorii z URL (np. /presentations/add/:categoryId)
  const { categoryId } = useParams();

  // Pobierz aktualną aplikację z kontekstu (zawiera contestId, applicationId, etc.)
  const { application } = useApplicationContext();

  // Pobierz dane kategorii na podstawie contestId i categoryId
  const { category } = useCategory(application?.contestId as string, categoryId as string);

  // ============================================================================
  // ZARZĄDZANIE STANEM ŁADOWANIA
  // ============================================================================

  // Sprawdź czy wszystkie wymagane dane są załadowane
  // Prezentacja nie może być utworzona bez kategorii i aplikacji
  const isDataLoading = !category || !application;

  // Wyświetl loading spinner jeśli dane nie są jeszcze gotowe
  // Zapobiega "jumpy" renderingowi i błędom z undefined data
  if (isDataLoading) {
    return <Loading />;
  }

  // ============================================================================
  // STRATEGY PATTERN - WYBÓR ODPOWIEDNIEGO FORMULARZA
  // ============================================================================

  // Dla kategorii SOLO: każdy zawodnik to osobna prezentacja
  // Formularz obsługuje wybór wielu zawodników, ale każdy zostanie zapisany jako osobna prezentacja
  if (category?.type === CategoryTypes.SOLO) {
    return (
      <SoloPresentationForm
        initialData={initialData}
        onSubmit={onSubmit}
        onCancel={onCancel}
        editableMode={editableMode}
        registrationBlocked={registrationBlocked}
      />
    );
  }

  // Dla wszystkich innych typów kategorii (DUO, TRIO, OTHER):
  // Grupa zawodników tworzy jedną prezentację
  // Formularz obsługuje wybór określonej liczby zawodników (min/max z kategorii)
  return (
    <GroupPresentationForm
      initialData={initialData}
      onSubmit={onSubmit}
      onCancel={onCancel}
      editableMode={editableMode}
      registrationBlocked={registrationBlocked}
    />
  );
}
